import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import { DateService } from 'common/services/dateService'
import dayjs, { type Dayjs } from 'dayjs'
import {
  SCHEDULE_TAGS,
  scheduleApi,
  useFetchScheduleDayQuery,
  useFetchScheduleMonthQuery,
} from 'features/Schedule/api/schedule.api'
import {
  scheduleGridQueryOptions,
  scheduleProfileQueryOptions,
  scheduleQueryOptions,
} from 'features/Schedule/api/scheduleQueryOptions'
import type { ScheduleMonth } from 'features/Schedule/api/schedule.types'
import { PathUtils } from 'routes/routes.utils'

import { Col, Checkbox, Row } from 'antd'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'

import { Icon } from '@iconify/react'
import { useAuth } from 'app/providers/auth/AuthProvider'
import { useAppDispatch } from 'common/hooks/redux'
import classNames from 'classnames'
import { Button } from 'common/components/Button/Button'
import Popup from 'common/components/Popup'
import { SelectCheckbox } from 'common/components/Select-Checkbox/select-checkbox'
import Show from 'common/components/Show2/Show'
import { BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import { LOCALE_STORAGE_KEYS, SESSION_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import { useTimezone } from 'common/hooks/useTimezone'
import {
  DEFAULT_SCHEDULE_ZOOM_INTERVAL,
  getNextZoomInInterval,
  getNextZoomOutInterval,
  type ScheduleZoomInterval,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/ZoomControls/scheduleZoomIntervals'
import { useMultipleChannels } from 'features/Schedule/pusher/MultiplePusherProvider'
import { useLocalStorage, useSessionStorage } from 'react-use'
import { compareScheduleStaffByRole } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'
import { ScheduleDayView } from './components/ScheduleDayView/ScheduleDayView'
import { ScheduleLoading } from './components/ScheduleLoading/ScheduleLoading'
import { ScheduleWeekView } from './components/ScheduleWeekView/ScheduleWeekView'
import styles from './schedule.module.scss'
import ScheduleHeaderCalendar from 'common/components/ScheduleHeaderCalendar/ScheduleHeaderCalendar'
import { useFetchProfileQuery } from 'features/SettingsOld/state/api/settingsApi'
import { INFO_CONSTANTS } from 'features/Home/constants/infoConstants'

const { getCurrentTimeInTimezone, preserveDateInTimezone } = DateService

dayjs.extend(isSameOrAfter)
dayjs.extend(customParseFormat)

export enum EScheduleView {
  DAY = 'day',
  WEEK = 'week',
}

const MAX_ACCUMULATED_SCHEDULE_DAYS = 400

const SchedulePage = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { setBookingIds } = useMultipleChannels()

  const { siteId, day } = useParams()
  const [calendarMonthDate, setCalendarMonthDate] = useState<string | null>(null)
  const { timeZone, isFetchingSites } = useTimezone()

  const viewDate = useMemo(() => {
    if (!timeZone) return null

    if (day && dayjs(day).isValid()) {
      return preserveDateInTimezone(day, timeZone).format(DATE_FORMAT.PATIENT_TRACKER_DATE)
    }

    return getCurrentTimeInTimezone(timeZone).format(DATE_FORMAT.PATIENT_TRACKER_DATE)
  }, [day, timeZone])

  const { data: profile } = useFetchProfileQuery(null, scheduleProfileQueryOptions)

  const [accumulatedScheduleData, setAccumulatedScheduleData] = useState<ScheduleMonth>({})

  const { hasEmrEnabled } = useAuth()

  const [currentView, setCurrentView] = useLocalStorage<EScheduleView>(
    LOCALE_STORAGE_KEYS.CURRENT_VIEW,
    EScheduleView.DAY,
  )

  const isDayView = currentView === EScheduleView.DAY
  const isWeekView = !isDayView

  useEffect(() => {
    if (currentView !== EScheduleView.DAY && currentView !== EScheduleView.WEEK) {
      setCurrentView(EScheduleView.WEEK)
    }
  }, [currentView, setCurrentView])

  const [selectedConsultationStaffIds, setSelectedConsultationStaffIds] = useLocalStorage<
    string[] | string | null
  >(LOCALE_STORAGE_KEYS.SELECTED_CONSULTATION_STAFF, null)

  const [showCanceled, setShowCanceled] = useSessionStorage<boolean>(
    SESSION_STORAGE_KEYS.SHOW_CANCELED_SCHEDULES,
    true,
  )

  const [selectedRoomIds, setSelectedRoomIds] = useSessionStorage<string[] | null>(
    SESSION_STORAGE_KEYS.SELECTED_SCHEDULE_ROOMS,
    null,
  )

  const [zoomInterval, setZoomInterval] = useState<ScheduleZoomInterval>(
    DEFAULT_SCHEDULE_ZOOM_INTERVAL,
  )

  const handleZoomIn = useCallback(() => {
    setZoomInterval((level) => getNextZoomInInterval(level))
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoomInterval((level) => getNextZoomOutInterval(level))
  }, [])

  const scheduleMonthQueryArgs = useMemo(() => {
    if (!siteId || !timeZone) return null

    const dateSource = calendarMonthDate ?? day
    const date = dateSource
      ? preserveDateInTimezone(dateSource, timeZone)
      : getCurrentTimeInTimezone(timeZone)

    return {
      siteId,
      startDate: date.startOf('month').add(-1, 'month').format(DATE_FORMAT.PATIENT_TRACKER_DATE),
      endDate: date.add(6, 'month').endOf('month').format(DATE_FORMAT.PATIENT_TRACKER_DATE),
    }
  }, [siteId, timeZone, calendarMonthDate, day])

  const {
    currentData: scheduleMonth,
    isLoading: isLoadingMonth,
    isFetching: isFetchingScheduleMonth,
  } = useFetchScheduleMonthQuery(scheduleMonthQueryArgs!, {
    skip: !scheduleMonthQueryArgs,
    ...scheduleQueryOptions,
  })

  const todayDate = useMemo(() => {
    if (!timeZone) return null
    return getCurrentTimeInTimezone(timeZone).format(DATE_FORMAT.PATIENT_TRACKER_DATE)
  }, [timeZone])

  const pendingTodayDayViewRef = useRef(false)

  const isPageLoading = isFetchingSites || !viewDate || isLoadingMonth

  const { currentData: scheduleDay } = useFetchScheduleDayQuery(
    { siteId, date: viewDate! },
    {
      skip: !siteId || !viewDate,
      ...scheduleGridQueryOptions,
    },
  )

  const scheduleRooms = useMemo(() => scheduleDay?.rooms ?? [], [scheduleDay?.rooms])

  const roomFilterOptions = useMemo(
    () =>
      [...scheduleRooms]
        .sort((a, b) => {
          if (a.is_waiting_room && !b.is_waiting_room) return -1
          if (!a.is_waiting_room && b.is_waiting_room) return 1
          return (a.order ?? 0) - (b.order ?? 0)
        })
        .map((room) => ({
          label: room.short_name || room.label,
          value: room.value,
        })),
    [scheduleRooms],
  )

  const doctorsUsers = useMemo(
    () =>
      (profile?.users?.filter((user) => user.is_doctor || user.is_provider) ?? []).sort(
        compareScheduleStaffByRole,
      ),
    [profile?.users],
  )

  useEffect(() => {
    setAccumulatedScheduleData({})
  }, [siteId])

  useEffect(() => {
    if (!scheduleMonth) return

    setAccumulatedScheduleData((prevData) => {
      const merged: ScheduleMonth = { ...prevData, ...scheduleMonth }
      const keys = Object.keys(merged).sort()

      if (keys.length <= MAX_ACCUMULATED_SCHEDULE_DAYS) {
        return merged
      }

      // keep the days closest to the freshly fetched window so it always survives the trim
      const anchor = Object.keys(scheduleMonth).sort()[0] ?? keys[0]
      const anchorTime = dayjs(anchor).valueOf()
      const keepKeys = new Set(
        [...keys]
          .sort(
            (a, b) =>
              Math.abs(dayjs(a).valueOf() - anchorTime) - Math.abs(dayjs(b).valueOf() - anchorTime),
          )
          .slice(0, MAX_ACCUMULATED_SCHEDULE_DAYS),
      )
      return Object.fromEntries(Object.entries(merged).filter(([key]) => keepKeys.has(key)))
    })
  }, [scheduleMonth])

  const allDays = useMemo(() => {
    if (!accumulatedScheduleData || Object.keys(accumulatedScheduleData).length === 0) return []
    return Object.keys(accumulatedScheduleData).sort()
  }, [accumulatedScheduleData])

  const availableDays = useMemo(() => {
    return allDays.filter((key) => accumulatedScheduleData[key]?.active)
  }, [allDays, accumulatedScheduleData])

  useEffect(() => {
    if (!timeZone || !siteId) return
    if ((!day || !dayjs(day).isValid()) && viewDate) {
      navigate(PathUtils.getSchedule(siteId, viewDate), { replace: true })
    }
  }, [day, timeZone, siteId, viewDate, navigate])

  const onChangeLeaseDate = useCallback(
    (date: string): void => {
      if (!timeZone || !siteId) return

      const formattedDate = preserveDateInTimezone(date, timeZone).format(
        DATE_FORMAT.PATIENT_TRACKER_DATE,
      )

      if (day !== formattedDate) {
        navigate(PathUtils.getSchedule(siteId, formattedDate))
      }
    },
    [siteId, timeZone, day, navigate],
  )

  const openDayView = useCallback(() => {
    setCurrentView(EScheduleView.DAY)
  }, [setCurrentView])

  const handleToday = useCallback(() => {
    if (!todayDate) return

    // Wait until the URL is today before mounting day view. Switching first
    // binds Syncfusion to the previous date's dataSource, then remounts on
    // today's selectedDate and the appointments never paint until a refresh.
    if (viewDate === todayDate) {
      openDayView()
      return
    }

    pendingTodayDayViewRef.current = true
    onChangeLeaseDate(todayDate)
  }, [todayDate, viewDate, openDayView, onChangeLeaseDate])

  useEffect(() => {
    if (!pendingTodayDayViewRef.current || !viewDate || !todayDate) return
    if (viewDate !== todayDate) return
    pendingTodayDayViewRef.current = false
    openDayView()
  }, [viewDate, todayDate, openDayView])

  const handleBook = () => {
    navigate(PathUtils.getBook(siteId))
  }

  const handleChangeWeek = (date: Dayjs) => {
    if (isWeekView) {
      onChangeLeaseDate(date.format(DATE_FORMAT.PATIENT_TRACKER_DATE))
    }
  }

  const handleWarningPopup = () => {
    Popup.showInfo(
      {
        title: 'No Booking Found!',
        message: INFO_CONSTANTS.NO_ACTIVE_SCHEDULE_TODAY,
        mainButtonText: 'Book',
      },
      () => handleBook(),
    )
  }

  const doctorsIds = useMemo(() => doctorsUsers.map((staff) => String(staff.id)), [doctorsUsers])

  useEffect(() => {
    if (!doctorsIds.length) return

    const stored = selectedConsultationStaffIds
    if (typeof stored === 'string') {
      if (stored === '-1' || stored === '') {
        setSelectedConsultationStaffIds(doctorsIds)
        return
      }
      setSelectedConsultationStaffIds(doctorsIds.includes(stored) ? [stored] : doctorsIds)
      return
    }

    if (stored == null) {
      setSelectedConsultationStaffIds(doctorsIds)
      return
    }

    if (stored.length === 0) return

    const validIds = stored.filter((staffId) => doctorsIds.includes(staffId))
    if (validIds.length === 0) {
      setSelectedConsultationStaffIds(doctorsIds)
      return
    }
    if (validIds.length !== stored.length) {
      setSelectedConsultationStaffIds(validIds)
    }
  }, [doctorsIds, selectedConsultationStaffIds, setSelectedConsultationStaffIds])

  useEffect(() => {
    if (!isDayView || !scheduleRooms.length) return
    if (selectedRoomIds == null) {
      setSelectedRoomIds(scheduleRooms.map((room) => room.value))
    }
  }, [isDayView, scheduleRooms, selectedRoomIds, setSelectedRoomIds])

  const availableRoomValues = useMemo(
    () => roomFilterOptions.map((room) => room.value),
    [roomFilterOptions],
  )

  const checkedRoomIds = useMemo(() => {
    if (!availableRoomValues.length || selectedRoomIds == null) {
      return availableRoomValues
    }

    return selectedRoomIds.filter((roomId) => availableRoomValues.includes(roomId))
  }, [availableRoomValues, selectedRoomIds])

  const handleRoomsFilterChange = useCallback(
    (nextRoomIds: Array<string | number>) => {
      const nextCurrentDayRoomIds = nextRoomIds.map(String)
      const availableSet = new Set(availableRoomValues)
      const preservedRoomIds = (selectedRoomIds ?? []).filter((roomId) => !availableSet.has(roomId))

      setSelectedRoomIds([...preservedRoomIds, ...nextCurrentDayRoomIds])
    },
    [availableRoomValues, selectedRoomIds, setSelectedRoomIds],
  )

  const consultationStaffOptions = useMemo(
    () =>
      doctorsUsers.map((staff) => ({
        label: staff.name,
        value: String(staff.id),
      })),
    [doctorsUsers],
  )

  const checkedStaffIds = useMemo(() => {
    if (!doctorsIds.length || !Array.isArray(selectedConsultationStaffIds)) {
      return doctorsIds
    }

    return selectedConsultationStaffIds.filter((staffId) => doctorsIds.includes(staffId))
  }, [doctorsIds, selectedConsultationStaffIds])

  const handleStaffFilterChange = useCallback(
    (nextStaffIds: Array<string | number>) => {
      setSelectedConsultationStaffIds(nextStaffIds.map(String))
    },
    [setSelectedConsultationStaffIds],
  )

  const selectedStaffFilterIds = useMemo(() => {
    if (!Array.isArray(selectedConsultationStaffIds)) return null
    if (checkedStaffIds.length === 0) return []
    if (doctorsIds.length > 0 && checkedStaffIds.length === doctorsIds.length) return null
    return checkedStaffIds
  }, [checkedStaffIds, doctorsIds.length, selectedConsultationStaffIds])

  useEffect(() => {
    if (doctorsIds.length) {
      setBookingIds(doctorsIds)
    }
  }, [doctorsIds, setBookingIds])

  useEffect(() => {
    return () => {
      dispatch(
        scheduleApi.util.invalidateTags([SCHEDULE_TAGS.SCHEDULE, SCHEDULE_TAGS.SCHEDULE_WEEK]),
      )
    }
  }, [dispatch])

  if (isPageLoading) {
    return (
      <Row className={styles.rowContainer}>
        <Col xs={24}>
          <ScheduleLoading />
        </Col>
      </Row>
    )
  }

  return (
    <Row className={styles.rowContainer}>
      <Col xs={24}>
        <Row
          id='schedule-header-calendar'
          justify={{ sm: 'start', xs: 'center' }}
          align={{ md: 'middle', xs: 'top' }}
          gutter={[12, 12]}>
          <Col xs={24}>
            <div className={styles.headerContainer}>
              <ScheduleHeaderCalendar
                onOpenChange={(open) => {
                  if (!open) {
                    setCalendarMonthDate(null)
                  }
                }}
                isFetching={isFetchingScheduleMonth}
                isLoading={false}
                defaultActiveDate={viewDate}
                availableDays={availableDays}
                scheduleData={accumulatedScheduleData}
                isWeekView={isWeekView}
                onDateChange={(date) => {
                  onChangeLeaseDate(date)
                }}
                handleClickDisabledDate={handleWarningPopup}
                onChangeMonth={(date) => {
                  setCalendarMonthDate(date.format(DATE_FORMAT.PATIENT_TRACKER_DATE))
                }}
                onChangeWeek={handleChangeWeek}
                timeZone={timeZone}
                forceEnableFutureDate
                forceEnablePastDate
              />

              <div className={styles.calendarViewSwitchContainer}>
                <Button
                  modifier={isDayView ? BUTTON_MODIFIER.PRIMARY : BUTTON_MODIFIER.DEFAULT}
                  className={classNames(
                    styles.calendarViewSwitch,
                    styles['calendarViewSwitch--not-selected'],
                  )}
                  onClick={handleToday}>
                  Today
                </Button>
                <Button
                  modifier={isDayView ? BUTTON_MODIFIER.PRIMARY : BUTTON_MODIFIER.DEFAULT}
                  className={classNames(
                    styles.calendarViewSwitch,
                    !isDayView && styles['calendarViewSwitch--not-selected'],
                  )}
                  onClick={() => {
                    pendingTodayDayViewRef.current = false
                    setCurrentView(EScheduleView.DAY)
                  }}>
                  Day
                </Button>
                <Button
                  modifier={isWeekView ? BUTTON_MODIFIER.PRIMARY : BUTTON_MODIFIER.DEFAULT}
                  className={classNames(
                    styles.calendarViewSwitch,
                    !isWeekView && styles['calendarViewSwitch--not-selected'],
                  )}
                  onClick={() => {
                    pendingTodayDayViewRef.current = false
                    setCurrentView(EScheduleView.WEEK)
                  }}>
                  Week
                </Button>
                <div className={styles.zoomControls}>
                  <Button className={styles.zoomButton} onClick={handleZoomOut}>
                    <Icon icon='ic:round-minus' width={18} height={18} />
                  </Button>
                  <Button className={styles.zoomButton} onClick={handleZoomIn}>
                    <Icon icon='ic:round-plus' width={18} height={18} />
                  </Button>
                </div>
                <div className={styles.staffSelect}>
                  <SelectCheckbox
                    selectValue={checkedStaffIds}
                    options={consultationStaffOptions}
                    checkBoxLabelKey='label'
                    checkBoxValueKey='value'
                    placeholder='Select consultation staff'
                    showSelectAll
                    onChange={handleStaffFilterChange}
                    disabled={!consultationStaffOptions.length}
                  />
                </div>
                {isDayView && hasEmrEnabled && (
                  <div className={styles.roomsSelect}>
                    <SelectCheckbox
                      selectValue={checkedRoomIds}
                      options={roomFilterOptions}
                      checkBoxLabelKey='label'
                      checkBoxValueKey='value'
                      placeholder='Select rooms'
                      showSelectAll
                      onChange={handleRoomsFilterChange}
                      disabled={!roomFilterOptions.length}
                    />
                  </div>
                )}
                <Checkbox
                  checked={showCanceled ?? true}
                  onChange={(e) => setShowCanceled(e.target.checked)}
                  className={styles.showCanceledCheckbox}>
                  Show canceled
                </Checkbox>
              </div>
            </div>
          </Col>
        </Row>
      </Col>

      <Show>
        <Show.When isVisible={isDayView}>
          <ScheduleDayView
            defaultActiveDate={viewDate}
            selectedConsultationStaffIds={selectedStaffFilterIds}
            selectedRoomIds={hasEmrEnabled ? selectedRoomIds : null}
            showCanceled={showCanceled ?? true}
            scheduleView={EScheduleView.DAY}
            scheduleMonthData={accumulatedScheduleData}
            zoomInterval={zoomInterval}
            staffRoleUsers={profile?.users}
          />
        </Show.When>
        <Show.Else>
          <ScheduleWeekView
            defaultActiveDate={viewDate}
            selectedConsultationStaffIds={selectedStaffFilterIds}
            showCanceled={showCanceled ?? true}
            scheduleView={EScheduleView.WEEK}
            scheduleMonthData={accumulatedScheduleData}
            zoomInterval={zoomInterval}
            staffRoleUsers={profile?.users}
          />
        </Show.Else>
      </Show>
    </Row>
  )
}

export default SchedulePage
